import React, { useMemo, useState } from "react";
import { BarChart, Callout, Card, Grid, LineChart, Title } from "@tremor/react";
import { Empty, Segmented, Skeleton, Statistic, Table, Tag, Tooltip } from "antd";
import dayjs from "dayjs";

import { getPriceFormatter } from "~/modules/Products/utils/getPriceFormatter";
import OverdueClientsTable from "./OverdueClientsTable";
import {
  useInstallmentCollectionStats,
  type InstallmentCohortSummary,
  type InstallmentCycleSummary,
} from "./useInstallmentCollectionStats";

const revenueFormatter = getPriceFormatter("PLN");
const formatAmount = (cents: number | null) => (cents === null ? "—" : revenueFormatter.format(cents / 100));
const formatRate = (rate: number | null) => (rate === null ? "—" : `${rate}%`);

const MONTH_OPTIONS = [
  { label: "6 mies.", value: 6 },
  { label: "12 mies.", value: 12 },
  { label: "24 mies.", value: 24 },
];

const rateClassName = (rate: number | null) =>
  rate === null ? "" : rate >= 90 ? "text-emerald-600" : rate >= 75 ? "text-amber-600" : "text-rose-600";

interface InstallmentsCollectionProps {
  productId: string;
}

export const InstallmentsCollection = ({ productId }: InstallmentsCollectionProps): React.ReactElement => {
  const [months, setMonths] = useState(12);

  const { data, isLoading, isError } = useInstallmentCollectionStats({ productId, months });

  const retentionSeries = useMemo(
    () =>
      (data?.retention ?? []).map((point) => ({
        rata: `Rata ${point.paymentCycle}`,
        Karta: point.cardRate,
        Manual: point.manualRate,
      })),
    [data],
  );

  const monthlySeries = useMemo(
    () =>
      (data?.monthly ?? []).map((month) => ({
        month: dayjs(`${month.month}-01`).format("MM.YYYY"),
        "Manual – opłacone": month.manual.paid,
        "Manual – po terminie": month.manual.overdue,
        "Manual – w terminie": month.manual.pending,
        "Karta – nieudane": month.card.failed,
      })),
    [data],
  );

  const cycleColumns = [
    {
      title: "Rata",
      dataIndex: "paymentCycle",
      key: "paymentCycle",
      render: (cycle: number) => <span className="font-medium">{cycle}</span>,
    },
    {
      title: "Karta – opłacone",
      key: "cardPaid",
      render: (_: unknown, row: InstallmentCycleSummary) => row.card.paid,
    },
    {
      title: "Karta – nieudane",
      key: "cardFailed",
      render: (_: unknown, row: InstallmentCycleSummary) => row.card.failed,
    },
    {
      title: "Karta – ściągalność",
      key: "cardRate",
      render: (_: unknown, row: InstallmentCycleSummary) => (
        <span className={rateClassName(row.card.collectionRate)}>{formatRate(row.card.collectionRate)}</span>
      ),
    },
    {
      title: "Manual – opłacone",
      key: "manualPaid",
      render: (_: unknown, row: InstallmentCycleSummary) => row.manual.paid,
    },
    {
      title: (
        <Tooltip title="Faktury wystawione, których termin płatności jeszcze nie minął">
          <span>Manual – w terminie</span>
        </Tooltip>
      ),
      key: "manualPending",
      render: (_: unknown, row: InstallmentCycleSummary) => row.manual.pending,
    },
    {
      title: (
        <Tooltip title="Faktury nieopłacone po terminie — cichy churn">
          <span>Manual – po terminie</span>
        </Tooltip>
      ),
      key: "manualOverdue",
      render: (_: unknown, row: InstallmentCycleSummary) =>
        row.manual.overdue ? <span className="font-medium text-rose-600">{row.manual.overdue}</span> : 0,
    },
    {
      title: "Manual – ściągalność",
      key: "manualRate",
      render: (_: unknown, row: InstallmentCycleSummary) => (
        <span className={rateClassName(row.manual.collectionRate)}>{formatRate(row.manual.collectionRate)}</span>
      ),
    },
  ];

  const cohortColumns = [
    {
      title: "Kohorta (start rat)",
      dataIndex: "cohortMonth",
      key: "cohortMonth",
      render: (month: string) => dayjs(`${month}-01`).format("MM.YYYY"),
    },
    {
      title: "Płatność",
      dataIndex: "kind",
      key: "kind",
      render: (kind: InstallmentCohortSummary["kind"]) =>
        kind === "manual" ? <Tag color="orange">Manual</Tag> : <Tag color="blue">Karta</Tag>,
    },
    { title: "Klienci", dataIndex: "totalClients", key: "totalClients" },
    {
      title: "≥ 2 raty",
      key: "reachedCycle2",
      render: (_: unknown, row: InstallmentCohortSummary) =>
        `${row.reachedCycle2} (${row.totalClients ? Math.round((row.reachedCycle2 / row.totalClients) * 100) : 0}%)`,
    },
    {
      title: "≥ 3 raty",
      key: "reachedCycle3",
      render: (_: unknown, row: InstallmentCohortSummary) =>
        `${row.reachedCycle3} (${row.totalClients ? Math.round((row.reachedCycle3 / row.totalClients) * 100) : 0}%)`,
    },
    {
      title: "≥ 6 rat",
      key: "reachedCycle6",
      render: (_: unknown, row: InstallmentCohortSummary) =>
        `${row.reachedCycle6} (${row.totalClients ? Math.round((row.reachedCycle6 / row.totalClients) * 100) : 0}%)`,
    },
    { title: "Śr. opłaconych rat", dataIndex: "avgPaidCycles", key: "avgPaidCycles" },
  ];

  if (isError) {
    return (
      <Callout title="Błąd" color="rose">
        Nie udało się pobrać statystyk ściągalności rat. Spróbuj ponownie później.
      </Callout>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-end">
        <Segmented value={months} onChange={(value) => setMonths(value as number)} options={MONTH_OPTIONS} />
      </div>

      <Skeleton active loading={isLoading} paragraph={{ rows: 12 }}>
        {data ? (
          <div className="flex flex-col gap-6">
            <Grid numItemsSm={2} numItemsLg={6} className="gap-4">
              <Card>
                <Statistic
                  title={
                    <Tooltip title="Faktury manual po terminie i faktury kartą, których Stripe już nie ponowi — tylko aktywne klientki">
                      <span>Zaległe faktury</span>
                    </Tooltip>
                  }
                  value={data.summary.overdueInvoices}
                  valueStyle={{ color: data.summary.overdueInvoices ? "#e11d48" : undefined }}
                />
              </Card>
              <Card>
                <Statistic title="Kwota po terminie" value={formatAmount(data.summary.overdueAmount)} />
              </Card>
              <Card>
                <Statistic title="Klientki z zaległością" value={data.summary.overdueClients} />
              </Card>
              <Card>
                <Statistic
                  title={
                    <Tooltip title="Faktury kartą, które Stripe jeszcze ponawia — nie są jeszcze stracone">
                      <span>W trakcie ponowień</span>
                    </Tooltip>
                  }
                  value={data.summary.retrying.invoices}
                />
                {data.summary.retrying.invoices ? (
                  <p className="mt-1 text-xs text-gray-500">{formatAmount(data.summary.retrying.amount)}</p>
                ) : null}
              </Card>
              <Card>
                <Statistic
                  title={
                    <Tooltip title="Nieopłacone faktury klientek już zarchiwizowanych — windykacja, nie utrzymanie">
                      <span>Zarchiwizowane z długiem</span>
                    </Tooltip>
                  }
                  value={data.summary.archived.clients}
                />
                {data.summary.archived.clients ? (
                  <p className="mt-1 text-xs text-gray-500">
                    {data.summary.archived.invoices} {data.summary.archived.invoices === 1 ? "faktura" : "faktur"} ·{" "}
                    {formatAmount(data.summary.archived.amount)}
                  </p>
                ) : null}
              </Card>
              <Card>
                <Statistic
                  title="Ściągalność"
                  value={`${formatRate(data.summary.cardCollectionRate)} · ${formatRate(data.summary.manualCollectionRate)}`}
                  valueStyle={{ fontSize: 20 }}
                  suffix={<span className="text-xs text-gray-500">karta · manual</span>}
                />
              </Card>
            </Grid>

            <Card>
              <Title>Zaległe płatności do obsłużenia</Title>
              <p className="mt-1 text-sm text-gray-500">
                Jedna sprawa = klientka i jej nieopłacone raty: faktury manual po terminie ({data.manualDueDays} dni)
                oraz faktury kartą, których Stripe nie zdołał pobrać. Nieudane pierwsze płatności w koszyku i faktury
                anulowane w Stripe nie są zaległością i nie pojawiają się tutaj.
              </p>
              <OverdueClientsTable items={data.overdue} />
            </Card>

            <Card>
              <Title>Ściągalność per numer raty</Title>
              <p className="mt-1 text-sm text-gray-500">
                Ściągalność = opłacone / (opłacone + nieudane + po terminie). Faktury w terminie nie obniżają wyniku.
              </p>
              {data.byCycle.length ? (
                <Table
                  className="mt-4"
                  size="small"
                  rowKey="paymentCycle"
                  columns={cycleColumns}
                  dataSource={data.byCycle}
                  pagination={false}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak danych" className="py-10" />
              )}
            </Card>

            <Grid numItemsLg={2} className="gap-6">
              <Card>
                <Title>Retencja ratalna: karta vs manual</Title>
                <p className="mt-1 text-sm text-gray-500">
                  % klientek docierających do raty N (tylko te, które miały na to czas i plan co najmniej N rat).
                </p>
                {retentionSeries.length ? (
                  <LineChart
                    className="mt-6 h-72"
                    data={retentionSeries}
                    index="rata"
                    categories={["Karta", "Manual"]}
                    colors={["indigo", "amber"]}
                    valueFormatter={(value) => `${value ?? 0}%`}
                    minValue={0}
                    maxValue={100}
                    yAxisWidth={50}
                  />
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak danych" className="py-10" />
                )}
              </Card>

              <Card>
                <Title>Faktury manual po miesiącach</Title>
                <p className="mt-1 text-sm text-gray-500">
                  Trend nieopłaconych faktur manual na tle opłaconych i nieudanych płatności kartą.
                </p>
                {monthlySeries.length ? (
                  <BarChart
                    className="mt-6 h-72"
                    data={monthlySeries}
                    index="month"
                    categories={[
                      "Manual – opłacone",
                      "Manual – po terminie",
                      "Manual – w terminie",
                      "Karta – nieudane",
                    ]}
                    colors={["emerald", "rose", "amber", "slate"]}
                    stack
                    allowDecimals={false}
                    yAxisWidth={40}
                  />
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak danych" className="py-10" />
                )}
              </Card>
            </Grid>

            <Card>
              <Title>Kohorty startu rat</Title>
              <p className="mt-1 text-sm text-gray-500">
                Klientki pogrupowane po miesiącu pierwszej opłaconej raty. „Manual" = przynajmniej jedna rata na
                fakturze manual.
              </p>
              {data.cohorts.length ? (
                <Table
                  className="mt-4"
                  size="small"
                  rowKey={(row) => `${row.cohortMonth}:${row.kind}`}
                  columns={cohortColumns}
                  dataSource={data.cohorts}
                  pagination={{ pageSize: 24, hideOnSinglePage: true }}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak danych" className="py-10" />
              )}
            </Card>

            <p className="text-right text-xs text-gray-500">
              Wygenerowano: {dayjs(data.generatedAt).format("DD.MM.YYYY HH:mm")} · zakres od{" "}
              {dayjs(data.fromDate).format("DD.MM.YYYY")}
            </p>
          </div>
        ) : null}
      </Skeleton>
    </div>
  );
};

export default InstallmentsCollection;
